import { useEffect, useRef } from "react";

export default function ParticleBackground() {
    const canvasRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0 });
    const animRef = useRef(null);

    useEffect(() => {
        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const isTouch = window.matchMedia("(pointer: coarse)").matches;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let w = window.innerWidth;
        let h = window.innerHeight;

        const resize = () => {
            w = window.innerWidth;
            h = window.innerHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = w + "px";
            canvas.style.height = h + "px";
            ctx.scale(dpr, dpr);
        };
        resize();

        const count = prefersReduced ? 36 : Math.min(isTouch ? 52 : 110, Math.floor((w * h) / (isTouch ? 24000 : 13000)));
        const particles = Array.from({ length: count }, () => ({
            x: Math.random() * w,
            y: Math.random() * h,
            vx: (Math.random() - 0.5) * 0.3,
            vy: (Math.random() - 0.5) * 0.3,
            r: Math.random() * 1.5 + 0.5,
            color: Math.random() > 0.5 ? "#3b82f6" : "#06b6d4",
            opacity: Math.random() * 0.3 + 0.1,
        }));

        const animate = () => {
            ctx.clearRect(0, 0, w, h);

            if (prefersReduced || isTouch) {
                particles.forEach(p => {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                    ctx.fillStyle = p.color;
                    ctx.globalAlpha = p.opacity;
                    ctx.fill();
                });
                ctx.globalAlpha = 1;
                return;
            }

            const mx = mouseRef.current.x;
            const my = mouseRef.current.y;
            const px = (mx - w / 2) * 0.007;
            const py = (my - h / 2) * 0.007;

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.x += p.vx + px * 0.02;
                p.y += p.vy + py * 0.02;

                if (p.x < -10) p.x = w + 10;
                if (p.x > w + 10) p.x = -10;
                if (p.y < -10) p.y = h + 10;
                if (p.y > h + 10) p.y = -10;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = p.color;
                ctx.globalAlpha = p.opacity;
                ctx.fill();

                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dx = p.x - p2.x;
                    const dy = p.y - p2.y;
                    const dist = dx * dx + dy * dy;
                    if (dist < 9200) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = p.color;
                        ctx.globalAlpha = 0.04 * (1 - Math.sqrt(dist) / 100);
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }
                }
            }
            ctx.globalAlpha = 1;
            animRef.current = requestAnimationFrame(animate);
        };

        animate();

        const onMove = (e) => {
            mouseRef.current = { x: e.clientX, y: e.clientY };
        };
        const onResize = () => {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            resize();
        };

        if (!isTouch) {
            window.addEventListener("mousemove", onMove);
        }
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(animRef.current);
            if (!isTouch) {
                window.removeEventListener("mousemove", onMove);
            }
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 z-0 pointer-events-none"
            style={{ background: "linear-gradient(180deg, #050b1a 0%, #0a0f1e 100%)" }}
            data-testid="particle-background"
        />
    );
}
